import React, { useState, useEffect, useMemo } from "react";
import { Download, Filter, RefreshCw } from "lucide-react";
import { enrichedLeadService } from "../services/enrichedLeadService.js";
import LeadCard, { listAgentContacts } from "../components/leads/LeadCard";
import LeadsFilters from "../components/leads/LeadsFilters";
import LeadDetailModal from "../components/leads/LeadDetailModal";
import StartAgentModal from "../components/agent/StartAgentModal";
import { useAuth } from "../context/AuthContext";

const VIEW_TABS = [
  {
    id: "ativos",
    label: "Ativos",
    idle: "bg-white text-slate-800 border border-slate-200 hover:bg-slate-50",
    active: "bg-slate-800 text-white border border-slate-700 shadow-sm",
  },
  {
    id: "convertidos",
    label: "Convertidos",
    idle: "bg-emerald-50 text-emerald-900 border border-emerald-200 hover:bg-emerald-100",
    active: "bg-emerald-600 text-white border border-emerald-500 shadow-sm",
  },
  {
    id: "perdidos",
    label: "Perdidos",
    idle: "bg-red-50 text-red-900 border border-red-200 hover:bg-red-100",
    active: "bg-red-600 text-white border border-red-500 shadow-sm",
  },
  {
    id: "rejeitados",
    label: "Inaptos",
    idle: "bg-amber-50 text-amber-950 border border-amber-200 hover:bg-amber-100",
    active: "bg-amber-600 text-white border border-amber-500 shadow-sm",
  },
  {
    id: "todos",
    label: "Todos",
    idle: "bg-white text-blue-900 border border-blue-200 hover:bg-blue-50",
    active: "bg-blue-600 text-white border border-blue-500 shadow-sm",
  },
];

const TERMINAL_STATUSES = ["CONVERTED", "LOST", "REJECTED"];

const VIEW_STATUS_MAP = {
  convertidos: "CONVERTED",
  perdidos: "LOST",
  rejeitados: "REJECTED",
};

const CONVERSION_DATE_KEYS = ["convertedAtStart", "convertedAtEnd"];
const LOST_DATE_KEYS = ["lostAtStart", "lostAtEnd"];

function hasFilterValue(value) {
  if (value === undefined || value === null || value === "") return false;
  if (Array.isArray(value) && value.length === 0) return false;
  return true;
}

function stripDateKeys(filters, keys) {
  const next = { ...filters };
  keys.forEach((key) => {
    delete next[key];
  });
  return next;
}

const Leads = () => {
  const { user } = useAuth();
  const [leads, setLeads] = useState([]);
  const [filters, setFilters] = useState({});
  const [filterOptions, setFilterOptions] = useState({
    tribunais: [],
    estados: [],
    esferas: [],
    provincias: [],
    cidades: [],
  });
  const [selectedView, setSelectedView] = useState("ativos");
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState("");
  const [selectedContacts, setSelectedContacts] = useState({});
  const [bulkContacts, setBulkContacts] = useState([]);

  const queryFilters = useMemo(() => {
    const next = { ...filters };
    delete next.status;
    delete next.excludeStatus;

    if (selectedView === "ativos") {
      next.excludeStatus = TERMINAL_STATUSES;
    } else if (VIEW_STATUS_MAP[selectedView]) {
      next.status = [VIEW_STATUS_MAP[selectedView]];
    }
    // "todos": sem restrição de status

    return next;
  }, [filters, selectedView]);

  useEffect(() => {
    loadFilterOptions();
  }, []);

  useEffect(() => {
    loadLeads();
  }, [queryFilters, currentPage]);

  useEffect(() => {
    setSelectedContacts({});
    setBulkContacts([]);
  }, [queryFilters]);

  const loadFilterOptions = async () => {
    try {
      const options = await enrichedLeadService.getFilterOptions();
      setFilterOptions(options);
    } catch (error) {
      console.error("Erro ao carregar opções de filtros:", error);
    }
  };

  const loadLeads = async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      const result = await enrichedLeadService.getEnrichedLeads(
        queryFilters,
        currentPage,
        20,
      );
      setLeads(result.leads || []);
      setTotal(result.total || 0);
      setTotalPages(Math.ceil((result.total || 0) / 20));
    } catch (error) {
      console.error("Erro ao carregar leads:", error);
      if (!silent) setLeads([]);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const removeLeadFromList = (leadId, processId) => {
    const removeIds = new Set(
      [leadId, processId].filter((id) => typeof id === "string" && id.length > 0),
    );
    if (removeIds.size === 0) return;

    setLeads((prev) =>
      prev.filter((l) => !removeIds.has(l.id) && !removeIds.has(l.processId)),
    );
    setTotal((prev) => Math.max(0, prev - 1));
    if (
      selectedLead &&
      (removeIds.has(selectedLead.id) || removeIds.has(selectedLead.processId))
    ) {
      setShowDetailModal(false);
      setSelectedLead(null);
    }
  };

  /** Atualiza a lista após ação de status; remove da aba atual quando o status sai dela. */
  const handleLeadUpdated = (event = {}) => {
    const { leadId, processId, action } = event;
    const viewStatus = VIEW_STATUS_MAP[selectedView];

    // Sai da aba atual (ex.: rejeitou em Ativos) → remove na hora, sem refetch fantasma
    if (
      action &&
      TERMINAL_STATUSES.includes(action) &&
      selectedView !== "todos" &&
      viewStatus !== action
    ) {
      removeLeadFromList(leadId, processId);
      return;
    }

    loadLeads({ silent: true });
  };

  const selectedContactList = Object.values(selectedContacts);

  function toggleContact(contact, checked) {
    setSelectedContacts((prev) => {
      const next = { ...prev };
      if (!checked) delete next[contact.leadId];
      else next[contact.leadId] = contact;
      return next;
    });
  }

  function setLeadContacts(rows, checked) {
    setSelectedContacts((prev) => {
      const next = { ...prev };
      for (const row of rows) {
        if (!row.leadId) continue;
        if (!checked) {
          delete next[row.leadId];
          continue;
        }
        next[row.leadId] = {
          leadId: row.leadId,
          name: row.name,
          primaryPhone: row.primaryPhone,
          inContact: row.inContact,
        };
      }
      return next;
    });
  }

  function selectPageContacts() {
    setSelectedContacts((prev) => {
      const next = { ...prev };
      for (const lead of leads) {
        for (const row of listAgentContacts(lead)) {
          if (!row.canStartAgent || !row.leadId) continue;
          next[row.leadId] = {
            leadId: row.leadId,
            name: row.name,
            primaryPhone: row.primaryPhone,
            inContact: row.inContact,
          };
        }
      }
      return next;
    });
  }

  async function handleBulkAgentStarted(_sessionId, _mode, result) {
    if (!result) return;
    const ids = [
      ...(result.started || []).map((item) => item.leadId),
      ...(result.failed || []).filter((item) => item.sessionId).map((item) => item.leadId),
    ];
    const assignedTo = enrichedLeadService.resolveContactUserName(user);
    await Promise.all(
      ids.map((leadId) =>
        enrichedLeadService.markInContact(leadId, assignedTo).catch(() => {}),
      ),
    );
    setSelectedContacts((prev) => {
      const next = { ...prev };
      for (const leadId of (result.started || []).map((item) => item.leadId)) {
        delete next[leadId];
      }
      return next;
    });
    loadLeads({ silent: true });
  }

  const handleViewChange = (viewId) => {
    setSelectedView(viewId);
    setCurrentPage(1);
    setFilters((prev) => {
      let next = { ...prev };
      if (viewId !== "convertidos" && viewId !== "todos") {
        next = stripDateKeys(next, CONVERSION_DATE_KEYS);
      }
      if (viewId !== "perdidos" && viewId !== "todos") {
        next = stripDateKeys(next, LOST_DATE_KEYS);
      }
      return next;
    });
  };

  const handleFilterChange = (key, value) => {
    if (key === "reset") {
      setFilters({});
      setSearchInput("");
      setSelectedView("ativos");
      setCurrentPage(1);
      return;
    }

    const filled = hasFilterValue(value);
    setFilters((prev) => {
      let next = { ...prev };
      if (!filled) {
        delete next[key];
        return next;
      }

      next[key] = value;
      if (CONVERSION_DATE_KEYS.includes(key)) {
        next = stripDateKeys(next, LOST_DATE_KEYS);
      }
      if (LOST_DATE_KEYS.includes(key)) {
        next = stripDateKeys(next, CONVERSION_DATE_KEYS);
      }
      return next;
    });

    if (filled && CONVERSION_DATE_KEYS.includes(key) && selectedView !== "convertidos") {
      setSelectedView("convertidos");
    }
    if (filled && LOST_DATE_KEYS.includes(key) && selectedView !== "perdidos") {
      setSelectedView("perdidos");
    }
    setCurrentPage(1);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const value = searchInput.trim();
    setFilters((prev) => {
      const next = { ...prev };
      if (value) next.search = value;
      else delete next.search;
      return next;
    });
    setCurrentPage(1);
  };

  const handleRefresh = () => {
    loadLeads();
  };

  const handleExportExcel = async () => {
    if (exporting || total === 0) return;
    setExporting(true);
    setExportError("");
    try {
      await enrichedLeadService.exportEnrichedLeadsExcel(queryFilters);
    } catch (error) {
      console.error("Erro ao exportar leads em Excel:", error);
      setExportError(
        error.response?.data?.message ||
          "Não foi possível exportar a planilha Excel.",
      );
    } finally {
      setExporting(false);
    }
  };

  const activeFilterCount = Object.keys(filters).filter(
    (k) =>
      k !== "search" &&
      filters[k] !== undefined &&
      filters[k] !== "" &&
      !(Array.isArray(filters[k]) && filters[k].length === 0),
  ).length;

  const handleLeadClick = (lead) => {
    setSelectedLead(lead);
    setShowDetailModal(true);
  };

  const emptyMessage = (() => {
    if (Object.keys(filters).length > 0) {
      return "Tente ajustar os filtros para encontrar mais leads.";
    }
    switch (selectedView) {
      case "convertidos":
        return "Nenhum lead convertido no momento.";
      case "perdidos":
        return "Nenhum lead perdido no momento.";
      case "rejeitados":
        return "Nenhum lead inapto no momento.";
      case "todos":
        return "Ainda não há leads gerados a partir de análises de processos.";
      default:
        return "Nenhum lead ativo no momento.";
    }
  })();

  return (
    <div className="space-y-4">
      <div>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Leads {total > 0 && `(${total})`}
            </h1>
            <p className="text-gray-600">
              Leads originados de processos analisados — um card por processo
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <button
              type="button"
              onClick={handleExportExcel}
              disabled={loading || exporting || total === 0}
              className="inline-flex items-center px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              <Download
                className={`w-4 h-4 mr-2 ${exporting ? "animate-pulse" : ""}`}
              />
              {exporting ? "Exportando..." : "Exportar Excel"}
            </button>
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              <RefreshCw
                className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`}
              />
              Atualizar
            </button>
          </div>
        </div>
        {exportError && (
          <p className="mt-2 text-sm text-red-600">{exportError}</p>
        )}

        {/* {metrics && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <MetricCard
              title="Total de Leads"
              value={metrics.total}
              icon="👥"
              subtitle="Processos analisados"
            />
            <MetricCard
              title="Valor Total Estimado"
              value={enrichedLeadService.formatCurrency(metrics.totalValue)}
              icon="💰"
              subtitle="Soma de valores"
            />
            <MetricCard
              title="Score Médio"
              value={metrics.averageScore.toFixed(0)}
              icon="⭐"
              subtitle="Qualidade média"
            />
            <MetricCard
              title="Leads com WhatsApp"
              value={metrics.withPhone}
              icon="📱"
              subtitle="Contatos diretos"
            />
          </div>
        )} */}

        <div className="space-y-3">
          <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm sticky top-4 z-20">
            <div className="flex flex-col lg:flex-row lg:items-center gap-3">
              <form onSubmit={handleSearchSubmit} className="flex flex-1 gap-2 min-w-0">
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Buscar por nome, CPF ou número do processo..."
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900"
                />
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 shrink-0"
                >
                  Buscar
                </button>
              </form>
              <div className="flex flex-wrap items-center gap-2 shrink-0">
                {VIEW_TABS.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => handleViewChange(tab.id)}
                    className={`px-3 py-2 rounded-xl text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer ${
                      selectedView === tab.id ? tab.active : tab.idle
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={() => setShowFilters((v) => !v)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors shrink-0 ${
                  activeFilterCount > 0 || showFilters
                    ? "bg-blue-600 text-white hover:bg-blue-700"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                <Filter className="w-4 h-4" />
                {showFilters ? "Ocultar filtros" : "Filtros"}
                {activeFilterCount > 0 && (
                  <span className="bg-blue-500 text-white text-xs px-1.5 py-0.5 rounded-full min-w-[1.25rem] text-center">
                    {activeFilterCount}
                  </span>
                )}
              </button>
            </div>
          </div>
          {showFilters && (
            <LeadsFilters
              filters={filters}
              onFilterChange={handleFilterChange}
              filterOptions={filterOptions}
              onClose={() => setShowFilters(false)}
              selectedView={selectedView}
              onViewChange={handleViewChange}
              viewTabs={VIEW_TABS}
            />
          )}
        </div>

        <div className="mt-6">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Carregando leads...</p>
            </div>
          ) : leads.length > 0 ? (
            <>
              <div className="flex justify-end mb-3">
                <button
                  type="button"
                  onClick={selectPageContacts}
                  className="text-sm font-medium text-emerald-700 hover:text-emerald-800"
                >
                  Selecionar contatos desta página
                </button>
              </div>
              <div className={`space-y-4 ${selectedContactList.length > 0 ? "pb-24" : ""}`}>
                {leads.map((lead) => (
                  <LeadCard
                    key={lead.id}
                    lead={lead}
                    onClick={handleLeadClick}
                    onLeadUpdated={handleLeadUpdated}
                    selectedContactIds={new Set(Object.keys(selectedContacts))}
                    onToggleContact={toggleContact}
                    onSelectLeadContacts={setLeadContacts}
                  />
                ))}
              </div>

              {totalPages > 1 && (
                <div className="bg-white rounded-lg shadow-sm mt-6 px-4 py-3 flex items-center justify-between border border-gray-200">
                  <div>
                    <p className="text-sm text-gray-700">
                      Mostrando{" "}
                      <span className="font-medium">
                        {(currentPage - 1) * 20 + 1}
                      </span>{" "}
                      a{" "}
                      <span className="font-medium">
                        {Math.min(currentPage * 20, total)}
                      </span>{" "}
                      de <span className="font-medium">{total}</span> processos
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                      <button
                        onClick={() =>
                          setCurrentPage(Math.max(currentPage - 1, 1))
                        }
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center px-3 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        ‹ Anterior
                      </button>

                      {Array.from(
                        { length: Math.min(totalPages, 5) },
                        (_, i) => {
                          let pageNum;
                          if (totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (currentPage <= 3) {
                            pageNum = i + 1;
                          } else if (currentPage >= totalPages - 2) {
                            pageNum = totalPages - 4 + i;
                          } else {
                            pageNum = currentPage - 2 + i;
                          }

                          return (
                            <button
                              key={pageNum}
                              onClick={() => setCurrentPage(pageNum)}
                              className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                currentPage === pageNum
                                  ? "z-10 bg-blue-50 border-blue-500 text-blue-600"
                                  : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                              }`}
                            >
                              {pageNum}
                            </button>
                          );
                        },
                      )}

                      <button
                        onClick={() =>
                          setCurrentPage(Math.min(currentPage + 1, totalPages))
                        }
                        disabled={currentPage === totalPages}
                        className="relative inline-flex items-center px-3 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Próxima ›
                      </button>
                    </nav>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-16 bg-white rounded-lg border-2 border-dashed border-gray-300">
              <div className="text-6xl mb-4">🔍</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Nenhum lead encontrado
              </h3>
              <p className="text-gray-500 max-w-md mx-auto">{emptyMessage}</p>
            </div>
          )}
        </div>
      </div>

      {selectedContactList.length > 0 && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 flex items-center gap-3 bg-gray-900 text-white rounded-lg shadow-lg px-4 py-3 max-w-xl">
          <span className="text-sm">
            {selectedContactList.length} contato{selectedContactList.length === 1 ? "" : "s"} selecionado{selectedContactList.length === 1 ? "" : "s"}
          </span>
          <button
            type="button"
            onClick={() => setSelectedContacts({})}
            className="text-sm text-gray-300 hover:text-white"
          >
            Limpar
          </button>
          <button
            type="button"
            onClick={() => setBulkContacts(selectedContactList)}
            className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 rounded-md text-sm font-medium"
          >
            Iniciar agente
          </button>
        </div>
      )}

      {bulkContacts.length > 0 && (
        <StartAgentModal
          contacts={bulkContacts}
          onClose={() => setBulkContacts([])}
          onStarted={handleBulkAgentStarted}
        />
      )}

      {showDetailModal && selectedLead && (
        <LeadDetailModal
          lead={selectedLead}
          onClose={() => setShowDetailModal(false)}
          onLeadUpdated={handleLeadUpdated}
        />
      )}
    </div>
  );
};

export default Leads;
